import * as React from 'react';

/**
 * Kamera kancasi.
 *
 * Neden ayri bir kanca: eski kod akisi alir almaz `setDurum('kamera')` deyip
 * `requestAnimationFrame` icinde `videoRef.current.srcObject` atiyordu.
 * `<video>` yalnizca o durumda basildigi icin rAF geri cagrisi React'in DOM'u
 * islemesinden ONCE kosarsa ref NULL oluyor ve akis hicbir yere baglanmiyordu:
 * izin verilmis, kamera yanmis, ekran ise bos. Masaustunde yaris genelde
 * kazaniliyordu, iOS Safari'de kaybediliyordu.
 *
 * Buradaki sozlesme yarisi tamamen kaldirir:
 *
 *   1) `<video>` HER ZAMAN DOM'da durur (gizliyken de). Ref asla null olmaz.
 *   2) `srcObject` `getUserMedia` doner donmez SENKRON atanir; rAF yok.
 *   3) `play()` beklenir, reddedilirse `autoplay` niteligi devreye girsin diye
 *      hata yutulur - ama asil olcut goruntunun GERCEKTEN gelmesidir.
 *   4) `acik` ancak `videoWidth > 0` olunca true olur. Boylece kare yakalama
 *      0x0 tuvale cizip bos JPEG gondermez.
 */

export interface Kamera {
  /** Cagiran bunu HER ZAMAN bir <video> uzerine baglamali (gizliyken de). */
  videoRef: React.RefObject<HTMLVideoElement | null>;
  /** Goruntu gercekten akiyor mu (videoWidth > 0). */
  acik: boolean;
  aciliyor: boolean;
  hata: string | null;
  ac: () => Promise<void>;
  kapat: () => void;
}

/** Goruntu gercekten gelene kadar bekler. iOS'ta ilk kare gecikebilir. */
function kareBekle(video: HTMLVideoElement, zamanAsimiMs = 8000): Promise<boolean> {
  if (video.videoWidth > 0) return Promise.resolve(true);
  return new Promise((coz) => {
    const basla = Date.now();
    const bak = () => {
      if (video.videoWidth > 0) return coz(true);
      if (Date.now() - basla > zamanAsimiMs) return coz(false);
      requestAnimationFrame(bak);
    };
    requestAnimationFrame(bak);
  });
}

/**
 * Tarayici hatasini gorevlinin anlayacagi bir cumleye cevirir.
 * Ozellikle `NotReadableError` onemli: iOS'ta kamera baska bir sekmede ya da
 * uygulamada acik kaldiginda bu doner ve "izin verin" demek yanlis yol gosterir.
 */
function hataMetni(e: unknown): string {
  const ad = (e as { name?: string })?.name;
  if (ad === 'NotAllowedError' || ad === 'SecurityError') {
    return 'Kamera izni verilmedi. Safari’de adres çubuğundaki «aA» → Web Sitesi Ayarları → Kamera’dan izin verip sayfayı yenileyin.';
  }
  if (ad === 'NotFoundError' || ad === 'OverconstrainedError') {
    return 'Bu cihazda kullanılabilir bir kamera bulunamadı. Galeriden fotoğraf seçebilirsiniz.';
  }
  if (ad === 'NotReadableError' || ad === 'AbortError') {
    return 'Kamera başka bir uygulama ya da sekme tarafından kullanılıyor. Onu kapatıp tekrar deneyin.';
  }
  return 'Kamera açılamadı. Tekrar deneyin ya da galeriden fotoğraf seçin.';
}

export function useKamera(): Kamera {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const akisRef = React.useRef<MediaStream | null>(null);
  const [acik, setAcik] = React.useState(false);
  const [aciliyor, setAciliyor] = React.useState(false);
  const [hata, setHata] = React.useState<string | null>(null);

  const kapat = React.useCallback(() => {
    akisRef.current?.getTracks().forEach((t) => t.stop());
    akisRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setAcik(false);
  }, []);

  // Sayfa kapanirken kamera isigi sonsuza dek yanmasin.
  React.useEffect(() => kapat, [kapat]);

  const ac = React.useCallback(async () => {
    setHata(null);
    setAciliyor(true);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setHata('Bu tarayıcı kamerayı desteklemiyor. Galeriden fotoğraf seçin.');
        return;
      }
      // Onceki akis acik kalirsa iOS ikincisini NotReadableError ile reddeder.
      akisRef.current?.getTracks().forEach((t) => t.stop());

      const akis = await navigator.mediaDevices.getUserMedia({
        // `ideal` cozunurluk telefonda dikey tutusla catisabiliyor; kamera
        // secimi disinda kisitlama koymuyoruz, kucultmeyi zaten kendimiz yapiyoruz.
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      akisRef.current = akis;

      const video = videoRef.current;
      if (!video) {
        // Kanca sozlesmesi ihlal edilmis: <video> DOM'da degil.
        akis.getTracks().forEach((t) => t.stop());
        akisRef.current = null;
        setHata('Kamera önizlemesi hazırlanamadı. Sayfayı yenileyin.');
        return;
      }

      video.srcObject = akis;
      // iOS: sessiz + satir ici olmayan video TAM EKRANA gecmeye calisir ve
      // otomatik oynatma reddedilir.
      video.muted = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      try {
        await video.play();
      } catch {
        // `autoplay` niteligi hala baslatabilir; asil olcut asagidaki kare.
      }

      if (!(await kareBekle(video))) {
        kapat();
        setHata('Kamera görüntüsü gelmedi. Tekrar deneyin ya da galeriden fotoğraf seçin.');
        return;
      }
      setAcik(true);
    } catch (e) {
      akisRef.current?.getTracks().forEach((t) => t.stop());
      akisRef.current = null;
      setHata(hataMetni(e));
    } finally {
      setAciliyor(false);
    }
  }, [kapat]);

  return { videoRef, acik, aciliyor, hata, ac, kapat };
}
